import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Star, TrendingUp, Target, Clock, Shield, Sparkles, Award, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

// Performance level definitions
const PERFORMANCE_LEVELS = [
  { name: 'Elite', min: 95, max: 100, color: 'text-amber-500', bgColor: 'bg-amber-500', lightBg: 'bg-amber-50', border: 'border-amber-200' },
  { name: 'Pro', min: 90, max: 94, color: 'text-emerald-600', bgColor: 'bg-emerald-500', lightBg: 'bg-emerald-50', border: 'border-emerald-200' },
  { name: 'Good', min: 80, max: 89, color: 'text-sky-600', bgColor: 'bg-sky-500', lightBg: 'bg-sky-50', border: 'border-sky-200' },
  { name: 'Standard', min: 70, max: 79, color: 'text-slate-600', bgColor: 'bg-slate-400', lightBg: 'bg-slate-50', border: 'border-slate-200' },
  { name: 'Low', min: 0, max: 69, color: 'text-red-600', bgColor: 'bg-red-500', lightBg: 'bg-red-50', border: 'border-red-200' }
];

// Get performance level from score
const getPerformanceLevel = (score) => {
  if (score === null || score === undefined) return null;
  return PERFORMANCE_LEVELS.find(level => score >= level.min && score <= level.max) || PERFORMANCE_LEVELS[4];
};

// Get next level info
const getNextLevel = (score) => {
  if (score === null || score === undefined) return null;
  const currentIndex = PERFORMANCE_LEVELS.findIndex(level => score >= level.min && score <= level.max);
  if (currentIndex <= 0) return null; // Already at Elite
  const nextLevel = PERFORMANCE_LEVELS[currentIndex - 1];
  const pointsNeeded = nextLevel.min - score;
  return { level: nextLevel, pointsNeeded: Math.ceil(pointsNeeded) };
};

// Helper function to extract score from breakdown value
// Handles both formats: direct number (80.0) or object ({score: 80, ...})
const getScoreValue = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.score;
  return val;
};

// Generate feedback messages based on performance
const generateFeedback = (breakdown, totalScore) => {
  const messages = [];
  
  if (!breakdown) return messages;
  
  // Punctuality feedback
  const punctuality = getScoreValue(breakdown.punctuality);
  if (punctuality !== null && punctuality !== undefined) {
    if (punctuality >= 95) {
      messages.push({ type: 'success', text: 'Excellent punctuality this month', icon: Clock });
    } else if (punctuality < 70) {
      messages.push({ type: 'warning', text: 'Attention: delays are lowering your score', icon: Clock });
    }
  }
  
  // Reliability feedback
  const reliability = getScoreValue(breakdown.reliability);
  if (reliability !== null && reliability !== undefined) {
    if (reliability >= 95) {
      messages.push({ type: 'success', text: 'Great reliability - keep it up!', icon: Shield });
    } else if (reliability < 70) {
      messages.push({ type: 'warning', text: 'Reliability issues detected', icon: Shield });
    }
  }
  
  // Ride Management feedback
  const management = getScoreValue(breakdown.ride_management);
  if (management !== null && management !== undefined) {
    if (management >= 90) {
      messages.push({ type: 'success', text: 'Ride management on point', icon: Target });
    } else if (management < 70) {
      messages.push({ type: 'warning', text: 'Remember to record all GPS checkpoints', icon: Target });
    }
  }
  
  // Quality feedback
  const quality = getScoreValue(breakdown.service_quality);
  if (quality !== null && quality !== undefined) {
    if (quality >= 95) {
      messages.push({ type: 'success', text: 'No operational issues reported', icon: Sparkles });
    } else if (quality < 80) {
      messages.push({ type: 'warning', text: 'Service quality needs attention', icon: Sparkles });
    }
  }
  
  // Overall feedback
  if (totalScore >= 95) {
    messages.push({ type: 'elite', text: 'Outstanding performance! You are among the best.', icon: Award });
  } else if (totalScore >= 90) {
    messages.push({ type: 'success', text: 'Great work! Keep pushing for Elite status.', icon: TrendingUp });
  }
  
  return messages.slice(0, 3); // Max 3 feedback messages
};

export default function DriverRatingCard({ compact = false }) {
  const { user, token } = useAuth();
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchRating();
  }, []);

  const fetchRating = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/drivers/${user.id}/rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRating(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching rating:', err);
      setError('Unable to load rating');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-12 bg-slate-200 rounded w-1/2 mb-4"></div>
        <div className="h-4 bg-slate-200 rounded w-full"></div>
      </div>
    );
  }

  if (error || !rating) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center gap-2 text-slate-500">
          <Star className="h-5 w-5" />
          <span className="text-sm">{error || 'No rating data available yet'}</span>
        </div>
      </div>
    );
  }

  const score = rating.average_score;
  const level = getPerformanceLevel(score);
  const nextLevelInfo = getNextLevel(score);
  const breakdown = rating.breakdown;
  const feedback = generateFeedback(breakdown, score);
  const totalRides = rating.total_rides || 0;

  // If no rides yet this month
  if (totalRides === 0 || score === null) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-xl">
            <Star className="h-4 w-4 text-slate-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">Performance Rating</h3>
            <p className="text-xs text-slate-400">No completed rides this month yet</p>
          </div>
        </div>
      </div>
    );
  }

  // Compact version for sidebar or small spaces
  if (compact) {
    return (
      <div className={`bg-gradient-to-br ${level?.lightBg || 'from-slate-50'} to-white rounded-xl p-4 shadow-sm border ${level?.border || 'border-slate-100'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${level?.bgColor || 'bg-slate-400'} rounded-lg`}>
              <Star className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900">{score.toFixed(0)}</span>
                <span className="text-sm text-slate-500">/ 100</span>
              </div>
              <span className={`text-xs font-medium ${level?.color}`}>{level?.name}</span>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </div>
      </div>
    );
  }

  // Full version - compact by default, expandable
  const punctuality = getScoreValue(breakdown?.punctuality);
  const rideManagement = getScoreValue(breakdown?.ride_management);
  const reliability = getScoreValue(breakdown?.reliability);
  const quality = getScoreValue(breakdown?.service_quality);

  return (
    <div className={`bg-gradient-to-br ${level?.lightBg || 'from-slate-50'} to-white rounded-2xl shadow-sm border ${level?.border || 'border-slate-100'} overflow-hidden`}>
      {/* Compact Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid="rating-card-toggle"
      >
        <div className={`p-2 ${level?.bgColor || 'bg-slate-400'} rounded-xl shadow-sm`}>
          <Star className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-slate-900">{score.toFixed(1)}</span>
            <span className="text-sm text-slate-400">/100</span>
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${level?.bgColor} text-white`}>
              {level?.name}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            {breakdown && (
              <>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3 text-sky-500" />{punctuality !== null ? Math.round(punctuality) : '-'}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Target className="h-3 w-3 text-violet-500" />{rideManagement !== null ? Math.round(rideManagement) : '-'}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Shield className="h-3 w-3 text-emerald-500" />{reliability !== null ? Math.round(reliability) : '-'}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Sparkles className="h-3 w-3 text-amber-500" />{quality !== null ? Math.round(quality) : '-'}</span>
              </>
            )}
            <span className="text-xs text-slate-400 ml-auto">{totalRides} rides</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-3">
          {/* Progress to Next Level */}
          {nextLevelInfo && (
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs text-slate-500">Progress to {nextLevelInfo.level.name}</span>
                <span className="text-xs font-medium text-slate-700">{nextLevelInfo.pointsNeeded} pts needed</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${nextLevelInfo.level.bgColor} rounded-full transition-all duration-500`}
                  style={{ 
                    width: `${Math.min(100, ((score - (getPerformanceLevel(score)?.min || 0)) / (nextLevelInfo.level.min - (getPerformanceLevel(score)?.min || 0))) * 100)}%` 
                  }}
                />
              </div>
            </div>
          )}

          {/* Performance Breakdown */}
          {breakdown && (
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/60 backdrop-blur rounded-lg p-2.5 border border-white/50">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Clock className="h-3.5 w-3.5 text-sky-500" />
                  <span className="text-xs text-slate-600">Punctuality</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{punctuality !== null ? Math.round(punctuality) : 'N/A'}</span>
                <span className="text-xs text-slate-400 ml-1">pts</span>
                <div className="mt-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full" style={{ width: `${punctuality || 0}%` }} />
                </div>
              </div>
              <div className="bg-white/60 backdrop-blur rounded-lg p-2.5 border border-white/50">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Target className="h-3.5 w-3.5 text-violet-500" />
                  <span className="text-xs text-slate-600">Ride Mgmt</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{Math.round(rideManagement || 0)}</span>
                <span className="text-xs text-slate-400 ml-1">pts</span>
                <div className="mt-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${rideManagement || 0}%` }} />
                </div>
              </div>
              <div className="bg-white/60 backdrop-blur rounded-lg p-2.5 border border-white/50">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Shield className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs text-slate-600">Reliability</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{Math.round(reliability || 0)}</span>
                <span className="text-xs text-slate-400 ml-1">pts</span>
                <div className="mt-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${reliability || 0}%` }} />
                </div>
              </div>
              <div className="bg-white/60 backdrop-blur rounded-lg p-2.5 border border-white/50">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs text-slate-600">Quality</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{Math.round(quality || 0)}</span>
                <span className="text-xs text-slate-400 ml-1">pts</span>
                <div className="mt-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${quality || 0}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Feedback Messages */}
          {feedback.length > 0 && (
            <div className="space-y-1.5">
              {feedback.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                    msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' :
                    msg.type === 'warning' ? 'bg-amber-50 text-amber-700' :
                    msg.type === 'elite' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700' :
                    'bg-slate-50 text-slate-700'
                  }`}
                >
                  <msg.icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{msg.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
